import React, { useEffect } from 'react'
import { Button } from '@/components/Buttons/Button'
import { AuthErrorType, LayoutType } from '@/actors/AuthActor/types'
import { TextInput } from '@/components/Inputs'
import { useForm } from 'react-hook-form'
import { useAuth } from '@/hooks/auth/useAuth'
import { useTranslation } from '@/localization/useTranslation'
import { EMAIL_REGEX } from '@/constants/auth'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { AuthErrorModal } from '@/actors/AuthActor/components/AuthErrorModal'
import { AuthSuccessModal } from '@/actors/AuthActor/components/AuthSuccessModal'

import classes from './styles.module.scss'

interface ResetPasswordProps {
  handleBack: () => void
  layoutType: LayoutType | null
  handleLayoutTypeChange: (type: LayoutType | null) => void
}

export const ResetPassword: React.FC<ResetPasswordProps> = (props) => {
  const { handleBack, layoutType, handleLayoutTypeChange } = props
  const { t } = useTranslation()
  const formTitle = t('Reset password')
  const { recoverPasswordMutation } = useAuth()
  const { reset, isPending, isSuccess, error } = recoverPasswordMutation
  const { register, handleSubmit, formState: { errors, isValid } } = useForm({
    defaultValues: {
      email: ''
    }
  })

  useEffect(() => {
    if (layoutType === 'island') return
    handleLayoutTypeChange('single-column')

    return () => {
      handleLayoutTypeChange(null)
    }
  }, [handleLayoutTypeChange, layoutType])

  const handleError = () => {
    reset()
  }

  const handleDismiss = () => {
    handleBack()
  }

  const onSubmit = async (data: { email: string }) => {
    try {
      await recoverPasswordMutation.mutateAsync(data)
    } catch (err) {
      console.error('Error during password reset:', err)
    }
  }

  return (
    <>
      <LoadingOverlay isLoading={isPending} />
      <AuthErrorModal
        error={error as unknown as AuthErrorType}
        handleError={handleError}
      />
      <AuthSuccessModal
        isVisible={isSuccess}
        type="success-reset-password"
        onDismiss={handleDismiss}
      />
      <header className={classes.header}>
        <h2 className={classes.title}>{formTitle}</h2>
        <h3 className={classes.subtitle}>{t('Enter your email to reset your password')}</h3>
        <h4 className={classes.message}>*{t('Required fields')}</h4>
      </header>
      <form action="#" onSubmit={handleSubmit(onSubmit)}>
        <TextInput
          errors={errors}
          type="email"
          required
          label={t('Email')}
          {...register('email', {
            required: {
              value: true,
              message: t('Email is required')
            },
            pattern: {
              value: EMAIL_REGEX,
              message: t('Invalid email')
            }
          })}
        />
        <Button type="submit" fullWidth disabled={!isValid}>
          {t('Send recovery email')}
        </Button>
        <Button variant="secondary" fullWidth onClick={handleBack}>
          {t('Back')}
        </Button>
      </form>
    </>

  )
}
